import React, { useState, useEffect } from 'react';
import { X, Save, Folder, Palette } from 'lucide-react';
import { Folder as FolderType } from '../../types'; // Keep this import
import { useClickOutside } from '../../hooks/useClickOutside';
import { useScrollLock } from '../../hooks/useScrollLock';

interface FolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (folderData: { name: string; parentId?: string; color?: string }) => void;
  onUpdate?: (id: string, updates: Partial<FolderType>) => void;
  folder?: FolderType | null;
  parentFolder?: FolderType | null;
}

const FOLDER_COLORS = [
  '#6B7280', // Gray
  '#EF4444', // Red
  '#F97316', // Orange
  '#EAB308', // Yellow
  '#22C55E', // Green
  '#06B6D4', // Cyan
  '#3B82F6', // Blue
  '#8B5CF6', // Purple
  '#EC4899', // Pink
];

export const FolderModal: React.FC<FolderModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onUpdate,
  folder,
  parentFolder,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    color: '#6B7280',
  });
  const [isLoading, setIsLoading] = useState(false);

  // Add error state for user feedback
  const [error, setError] = useState<string | null>(null);
  const modalRef = useClickOutside(() => {
    if (isOpen && !isLoading) {
      onClose();
    }
  });

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  useEffect(() => {
    if (folder) {
      setFormData({
        name: folder.name,
        color: folder.color,
      });
      setError(null);
    } else {
      setFormData({
        name: '',
        color: '#6B7280',
      });
      setError(null);
    }
  }, [folder, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Please enter a folder name');
      return;
    }

    console.log('🔵 FolderModal: Starting folder creation/update', {
      isEditing: !!folder,
      formData,
      parentFolder
    });
    setIsLoading(true);
    setError(null);
    
    try {
      if (folder && onUpdate) {
        console.log('🔵 FolderModal: Updating existing folder', folder.id);
        await onUpdate(folder.id, {
          name: formData.name.trim(),
          color: formData.color,
        });
        console.log('✅ FolderModal: Folder updated successfully');
      } else {
        console.log('🔵 FolderModal: Creating new folder');
        await onSave({
          name: formData.name.trim(),
          parentId: parentFolder?.id,
          color: formData.color,
        });
        console.log('✅ FolderModal: Folder created successfully');
      }
    } catch (err) {
      console.error('Error saving folder:', err);
      setError(err instanceof Error ? err.message : 'Failed to save folder. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div ref={modalRef} className="bg-white dark:bg-dark-surface rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center">
            <Folder className="h-6 w-6 text-modular-frosted-indigo dark:text-modular-frosted-indigo mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text">
                {folder ? 'Edit Folder' : 'New Folder'}
              </h2>
              {parentFolder && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  in {parentFolder.name}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Folder Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Folder Name
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} // Keep this action
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-fire-500 focus:border-transparent bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text"
              placeholder="Enter folder name..."
              required
              autoFocus
            />
          </div>

          {/* Color Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
              <Palette className="h-4 w-4 mr-2" />
              Folder Color
            </label>
            <div className="grid grid-cols-9 gap-2"> {/* Keep this grid */}
              {FOLDER_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData({ ...formData, color })} // Keep this action
                  className={`w-8 h-8 rounded-lg border-2 transition-all duration-200 ${
                    formData.color === color // Keep this logic
                      ? 'border-modular-frosted-indigo dark:border-modular-frosted-indigo scale-110'
                      : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-dark-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.name.trim()} // Keep this logic
              className="bg-fire-gradient text-white px-6 py-2 rounded-lg hover:shadow-fire-glow focus:outline-none focus:ring-2 focus:ring-fire-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {folder ? 'Update' : 'Create'} Folder
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};